/**
 * QuestionCard.jsx
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Timer from './Timer';
import MultipleChoice from './MultipleChoice';
import TypedAnswer from './TypedAnswer';
import PlayerPhoto from './PlayerPhoto';
import { isMatch } from '../utils/fuzzyMatch';
import { getAltColleges } from '../data/altColleges';

const FEEDBACK_DELAY = 1200;
const EASY_TIMER = 7;
const HARD_TIMER = 7;
const SUDDEN_DEATH_TIMER = 7;

const QuestionCard = ({ question, mode, phase, onAnswer }) => {
  const [locked, setLocked] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const lockedRef = useRef(false);
  const feedbackTimeoutRef = useRef(null);

  const questionKey = `${question.player}-${question.index}`;

  const timerDuration =
    phase === 'sudden_death'
      ? SUDDEN_DEATH_TIMER
      : mode === 'easy'
        ? EASY_TIMER
        : HARD_TIMER;

  useEffect(() => {
    setLocked(false);
    setSelectedAnswer(null);
    setFeedback(null);
    lockedRef.current = false;
    document.activeElement?.blur();

    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, [questionKey]);

  const handleAnswer = useCallback(
    (answer, correct) => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setLocked(true);
      setSelectedAnswer(answer);
      setFeedback(correct ? 'correct' : 'incorrect');

      feedbackTimeoutRef.current = setTimeout(() => {
        onAnswer(answer, correct);
      }, FEEDBACK_DELAY);
    },
    [onAnswer]
  );

  const handleMultipleChoiceSelect = useCallback(
    (choice) => {
      const correct = choice === question.college;
      handleAnswer(choice, correct);
    },
    [handleAnswer, question.college]
  );

  const handleTypedSubmit = useCallback(
    (typedValue) => {
      // Transfers: any UNDERGRAD school the player attended is a correct answer.
      // Postgrad schools (law/MBA/med) are deliberately NOT accepted - the game is
      // about undergrad - but they are still kept out of the multiple-choice options.
      const schools = [question.college, ...getAltColleges(question.player)];
      const correct = schools.some((school) => isMatch(typedValue, school));
      if (correct) {
        handleAnswer(typedValue, true);
      } else {
        // Wrong — buzz but keep timer running, let them try again
        setFeedback('incorrect');
        setTimeout(() => setFeedback(null), 200);
      }
    },
    [handleAnswer, question.college, question.player]
  );

  const handleTimerExpire = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    setSelectedAnswer(null);
    setFeedback('incorrect');

    feedbackTimeoutRef.current = setTimeout(() => {
      onAnswer(null, false);
    }, FEEDBACK_DELAY);
  }, [onAnswer]);

  const isTyped = mode === 'hard';

  return (
    <div className={`question-card ${feedback ? `feedback-${feedback}` : ''}`}>
      <Timer
        duration={timerDuration}
        onExpire={handleTimerExpire}
        isPaused={locked}
        questionKey={questionKey}
      />

      <div className="player-name-container">
  <div style={{ width: '100%', height: '145px', overflow: 'hidden', background: '#111b30', borderRadius: '4px' }}>
    <PlayerPhoto
      player={question}
      className="player-photo"
    />
  </div>
        <h2 className="player-name">{question.player}</h2>
        {question.category && (
          <p style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--gold)', letterSpacing: '2px', margin: '2px 0 4px 0', opacity: 0.8 }}>
            {question.category.toUpperCase()}
          </p>
        )}
      
<p className="player-prompt">
          {isTyped ? 'Type the college this player attended' : 'Which college did this player attend?'}
        </p>
      </div>

      {isTyped ? (
        <TypedAnswer
          onSubmit={handleTypedSubmit}
          correctAnswer={question.college}
          locked={locked}
          feedback={feedback}
          questionKey={questionKey}
        />
      ) : (
        <MultipleChoice
  key={questionKey}
  choices={question.choices}
  onSelect={handleMultipleChoiceSelect}
  selectedAnswer={locked ? selectedAnswer : null}
  correctAnswer={question.college}
  locked={locked}
/>
      )}
    </div>
  );
};

export default QuestionCard;